import { FC, useEffect, useState } from 'react';
import './App.css';
import 'antd/dist/reset.css';
import 'react-json-view-lite/dist/index.css';
import { ConfigProvider, Layout, Menu, theme } from 'antd';
import { Content, Footer, Header } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import { Link, Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { ControlOutlined, FolderOpenOutlined, FundViewOutlined, HomeOutlined } from '@ant-design/icons';
import { ThimphuMessageDispatcher } from './components/dispatchers/noaThimphuMessageDispatcher';
import { FlexCol, FlexRow } from './components/common';
import { RefkitMessageDispatcher } from './components/dispatchers/noaRefkitMessageDispatcher';
import { NoaDevicePage } from './components/pages/noaDevicePage';
import { NoaConnectPage } from './components/pages/noaConnectPage';
import { NoaSensePage } from './components/pages/noaSensePage';
import { RecordsPage } from './components/pages/recordsPage';
import { RecordPage } from './components/pages/recordPage';
import { NoaDebugPage } from './components/pages/noaDebugPage';
import { HomePage } from './components/pages/homePage';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ProfilePage } from './components/pages/profilePage';
import { CsmDebugPage } from './components/pages/csmDebugPage';
import { CsmBleConnectPage } from './components/pages/csmBleConnectPage';
import { CsmSensePage } from './components/pages/csmSensePage';
import { CsmDevicePage } from './components/pages/csmDevicePage';
import { ApplicationDeviceType, getApplicationDeviceType as getApplicationDeviceType, useMediaQuery } from './components/utils';
import { CsmSerialMessageDispatcher } from './components/dispatchers/csmSerialMessageDispatcher';
import { CsmSerialConnectPage } from './components/pages/csmSerialConnectPage';
import { CsmBleMessageDispatcher } from './components/dispatchers/csmBleMessageDispatcher';
import { Auth0Provider, useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { LogoutWidget } from './components/widgets/LogoutWidget';

const SIDER_WIDTH = 250;
const SIDER_COLLAPSED_WIDTH = 80;

const AppMenu: FC<{
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
}> = ({ isCollapsed, setIsCollapsed }) => {
  const isMobile = useMediaQuery();

  return (
    <Sider
      style={
        isMobile
          ? {
              overflow: 'auto',
              position: 'fixed',
              width: '100%',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
            }
          : {
              overflow: 'auto',
              height: '100vh',
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
            }
      }
      collapsible={isMobile ? false : true}
      collapsed={isCollapsed}
      onCollapse={(collapsed) => {
        setIsCollapsed(collapsed);
      }}
      collapsedWidth={isMobile ? '100%' : undefined}
      width={isMobile ? '100%' : SIDER_WIDTH}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          gap: 0,
        }}
      >
        {!isMobile && (
          <FlexCol
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
            }}
          >
            <FlexRow
              style={{
                height: '64px',
                justifyContent: 'center',
              }}
            >
              {isCollapsed ? (
                <img
                  alt="logo"
                  src="/images/logo292.png"
                  style={{
                    padding: 10,
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                />
              ) : (
                <img
                  alt="logo"
                  src="/images/aryballe-tm-white-transparent.png"
                  style={{
                    padding: 5,
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                />
              )}
            </FlexRow>
            {!isCollapsed && <span style={{ color: '#eee' }}>NeOse WebApp</span>}
          </FlexCol>
        )}
        <Menu
          theme="dark"
          mode={isMobile ? 'horizontal' : 'vertical'}
          style={{
            width: '100%',
          }}
          defaultActiveFirst
          items={[
            {
              key: 'menu-key-home',
              icon: <HomeOutlined />,
              label: <Link to="/">Home</Link>,
            },
            {
              key: 'menu-key-sense',
              icon: <FundViewOutlined />,
              label: <Link to={`/sense/${getApplicationDeviceType()}`}>Sense</Link>,
            },
            {
              key: 'menu-key-records',
              icon: <FolderOpenOutlined />,
              label: <Link to={`/records`}>Records</Link>,
            },
            {
              key: 'menu-key-device',
              icon: <ControlOutlined />,
              label: <Link to={`/device/${getApplicationDeviceType()}`}>Device</Link>,
            },
          ]}
        />
        <FlexRow
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? undefined : '100%',
            gap: 0,
          }}
        >
          {(function () {
            switch (getApplicationDeviceType()) {
              case ApplicationDeviceType.Noa:
                return (
                  <>
                    <div
                      style={{
                        borderTop: '1px solid #ccc',
                        borderRight: '1px solid #ccc',
                      }}
                    >
                      <RefkitMessageDispatcher minimized={isCollapsed} />
                    </div>
                    <div
                      style={{
                        borderTop: '1px solid #ccc',
                      }}
                    >
                      <ThimphuMessageDispatcher minimized={isCollapsed} />
                    </div>
                  </>
                );
              case ApplicationDeviceType.CsmSerial:
                return (
                  <div>
                    <CsmSerialMessageDispatcher minimized={isCollapsed} />
                  </div>
                );
              case ApplicationDeviceType.CsmBle:
                return (
                  <div>
                    <CsmBleMessageDispatcher minimized={isCollapsed} />
                  </div>
                );
              default:
                return null;
            }
          })()}
        </FlexRow>
      </div>
    </Sider>
  );
};

const App: FC = () => {
  const { user } = useAuth0();
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    window.addEventListener('DOMContentLoaded', () => {
      let displayMode = 'browser-tab';
      if (window.matchMedia('(display-mode: standalone)').matches) {
        displayMode = 'standalone';
      }
      console.log('app root: display mode is', displayMode);
      if (displayMode === 'browser-tab') {
        toast("You can install this app on your device by clicking on the 'Add to Home Screen' button in your browser menu.");
      }
    });

    // return () => {
    //   window.removeEventListener('DOMContentLoaded', () => {})
    // }
  }, []);

  const isMobile = useMediaQuery();

  const minHeightOffset = isMobile ? 200 : 114;

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: 'rgb(32, 42, 68)',
          colorLink: 'rgb(32, 42, 68)',
          fontFamily: 'Century Gothic',
          fontSize: 13,
          colorTextBase: '#222',

          // These are the colors of table selection
          colorPrimaryBg: 'rgba(32, 42, 68, 0.2)',
          colorPrimaryBgHover: 'rgba(32, 42, 68, 0.4)',
        },
      }}
    >
      <Layout>
        <AppMenu isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
        <Layout
          className="site-layout"
          style={{
            marginLeft: isMobile ? 0 : isCollapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH,
          }}
        >
          <Header
            style={{
              padding: 0,
              background: colorBgContainer,
              position: 'sticky',
              top: 0,
              zIndex: 500,
            }}
          >
            <FlexRow
              style={{
                justifyContent: isMobile ? 'space-between' : 'end',
                alignItems: 'center',
                height: '100%',
                paddingRight: 20,
              }}
            >
              {isMobile && (
                <FlexCol
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0,
                  }}
                >
                  <FlexRow
                    style={{
                      height: '64px',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      alt="logo"
                      src="/images/logo292.png"
                      style={{
                        padding: 10,

                        maxWidth: '100%',
                        maxHeight: '100%',
                      }}
                    />
                  </FlexRow>
                </FlexCol>
              )}
              <LogoutWidget />
            </FlexRow>
          </Header>

          <Content
            style={{
              marginTop: 16,
              marginLeft: 16,
              marginRight: 16,
              marginBottom: 24,
              padding: isMobile ? 0 : 24,
              overflow: 'initial',
              minHeight: `calc(100vh - ${minHeightOffset}px)`,
            }}
          >
            <Switch>
              <Route path="/signin/callback">
                <Redirect to={window.location.pathname} />
              </Route>

              <Route path="/profile">
                <ProfilePage />
              </Route>

              <Route path="/device/csm">
                <CsmDevicePage />
              </Route>

              <Route path="/connect/noa">
                <NoaConnectPage />
              </Route>

              <Route path="/connect/csm/ble">
                <CsmBleConnectPage />
              </Route>

              <Route path="/connect/csm/serial">
                <CsmSerialConnectPage />
              </Route>

              <Route path="/sense/noa">
                <NoaSensePage />
              </Route>

              <Route path="/sense/csm">
                <CsmSensePage />
              </Route>

              <Route path="/records/:recordId">
                <RecordPage />
              </Route>

              <Route path="/records">
                <RecordsPage />
              </Route>

              <Route path="/debug/noa">
                <NoaDebugPage />
              </Route>

              <Route path="/debug/csm">
                <CsmDebugPage />
              </Route>

              <Route path="/debug/csm">
                <CsmDebugPage />
              </Route>

              <Route path="/">
                <HomePage />
              </Route>
            </Switch>
          </Content>
          <Footer
            style={{
              textAlign: 'center',
              height: '50px',
            }}
          >
            © Aryballe {new Date().getFullYear()} — ver. <b>{process.env.REACT_APP_VER != null && `${process.env.REACT_APP_VER}`}</b> {process.env.REACT_APP_ENV != null && `[${process.env.REACT_APP_ENV.toUpperCase()}]`}
          </Footer>
        </Layout>
      </Layout>
      <ToastContainer />
    </ConfigProvider>
  );
};

const Root: React.FC = () => {
  return (
    <Auth0Provider
      domain="https://login.aryballe.com"
      clientId="CVch796WLukfGM4X1IpC3NIcUlUp9lTQ"
      authorizationParams={{
        redirect_uri: window.location.origin + '/signin/callback',
        scope: 'openid email profile assigned_groups',
        audience: 'https://tools.aryballe.com/api/',
      }}
    >
      <Switch>
        <Route path="/signout/callback">
          <Redirect to="/" />
        </Route>
        <Route path="/" component={withAuthenticationRequired(App)} />
      </Switch>
    </Auth0Provider>
  );
};
export default Root;
